import type {
    ComponentSchemasDefinition,
    ComponentDefinition,
    SchemasCollection,
    CoreSchemaConfig,
    SchemaService,
    SchemaConfig,
    SchemaFile,
    NamespaceConfig,
    DSConfig
} from '@wix/document-services-types'
import _ from 'lodash'
import common from '../dist/schemas/common.json'
import {DATA_TYPES} from '../common/namespaceMapping'
import theSchemasFormerlyKnownAsCommon from '../dist/schemas/commonSchemas.json'
import {extendDataNodeSchemas} from './schemas/schemaUtils'
import {CannotFindSchemaError, SchemaValidationError, SchemaValidationErrorDetails} from './schemas/errors'
import {createSchemaCore, SchemaServiceOptions, ValidationFunction} from './schemas/schemaCore'
import {getUnifiedSchemasConfig} from './configs/schemaConfigs'
import * as whitelistCleanup from './schemas/whitelistCleanup'
// eslint-disable-next-line import/no-unresolved
import {coreUtils} from '@wix/santa-ds-libs/basic'
import {getCssFormats} from './configs/cssFormats'

const {isValidFontFamily} = coreUtils.fontUtils.createFontUtils()
const APP_VARS_TYPE = 'AppVars'
const notActuallySchemas = [
    'definition',
    'componentsDefinitionsMap',
    'allComponentsDefinitionsMap',
    'componentTypeAliases',
    'skinsByComponentType',
    'containers'
]

function isAppVarsType(type?: string): boolean {
    return type === APP_VARS_TYPE
}

const getSchemasFromConfig = (schemasAndDefinitionsArrangedByDataType: any) => ({
    ..._.omit(schemasAndDefinitionsArrangedByDataType, notActuallySchemas),
    theSchemasFormerlyKnownAsCommon,
    common
})

const getSchemasFromConfigRestrictedAndReferenced = (schemasAndDefinitionsArrangedByDataType: any) => ({
    ..._.omit(schemasAndDefinitionsArrangedByDataType, notActuallySchemas),
    // we need to change the name of the commonSchemas to theSchemasFormerlyKnownAsCommon
    // because it needs to match the names in schemas object.
    // we could try and improve it
    theSchemasFormerlyKnownAsCommon: schemasAndDefinitionsArrangedByDataType.commonSchemas
})

function createService(schemaConfig: SchemaConfig, schemaServiceOptions?: SchemaServiceOptions): SchemaService {
    const coreSchemaConfig = {
        namespaces: getSchemasFromConfig(schemaConfig.schemas),
        permanentDataTypes: schemaConfig.permanentDataTypes,
        formats: {
            'font-family': isValidFontFamily,
            ...getCssFormats()
        }
    } as CoreSchemaConfig
    if (schemaConfig.restrictedNonInflatedSchemas) {
        coreSchemaConfig.restrictedNonInflatedSchemas = getSchemasFromConfigRestrictedAndReferenced(
            schemaConfig.restrictedNonInflatedSchemas
        )
    }
    const compDefs = {...schemaConfig.schemas.definition}
    const systemStyleIds = new Set<string>()
    const registeredBuilderCollections = new Set<string>()

    _.forOwn(compDefs, definition => {
        if (definition.styles) {
            Object.keys(definition.styles).forEach(styleId => systemStyleIds.add(styleId))
        }
    })

    const aliasToCompKeyMap = new Map<string, string>()
    function getDefinition(componentType: string) {
        return compDefs[componentType] ?? compDefs[aliasToCompKeyMap.get(componentType)!]
    }

    function isBuilderType(type?: string) {
        return type && registeredBuilderCollections.has(type)
    }

    const getComponentType = (compKey: string) => getDefinition(compKey)?.type

    const updateAliasMap = (compKey: string) => {
        const compDef = compDefs[compKey]
        if (compDef?.aliases) {
            compDef.aliases.forEach((alias: string) => {
                aliasToCompKeyMap.set(alias, compKey)
            })
        }
    }

    const updateSystemStyles = (componentDefinition: Record<string, any>) => {
        if (componentDefinition.styles) {
            Object.keys(componentDefinition.styles).forEach((styleId: string) => {
                systemStyleIds.add(styleId)
            })
        }
    }

    Object.keys(compDefs).forEach((compKey: string) => {
        updateAliasMap(compKey)
    })

    const core = createSchemaCore(coreSchemaConfig, schemaConfig.references, schemaServiceOptions)

    const getSchema = (namespace: string, schemaName: string): any => {
        const cachedSchema = core.getInflatedSchemaFromCache(namespace, schemaName)
        if (cachedSchema) {
            return cachedSchema
        }
        const schema = _.get(schemaConfig.schemas, [namespace, schemaName])
        if (!schema) {
            return schema
        }
        const inflatedSchema = core.inflateSchema(namespace, schema, schemaName)
        core.addInflatedSchemaToCache(namespace, schemaName, inflatedSchema)
        return inflatedSchema
    }

    function getDefinitionByPredicate(lodashPredicate: any) {
        return _.find(compDefs, lodashPredicate)
    }

    /** Return a boolean representing whether or not the given `id` is the id of a system style
     *
     * A system style is any style that exists in a component definitions's `styles` property.
     * Note that while most components are supplied at service initialization, some
     * may be registered at runtime, which might change the results of this function on certain inputs.
     *
     * @param {string} id The id of the style
     * @returns {boolean} true if `id` is the id of a system style, false otherwise
     */
    function isSystemStyle(id: string): boolean {
        return systemStyleIds.has(id)
    }

    const createValidator =
        (coreValidator: ValidationFunction) => (dataTypeName: string, data: any, namespace: string) => {
            const designDt = DATA_TYPES.design
            const dataDt = DATA_TYPES.data

            if (
                isAppVarsType(data?.type) ||
                (schemaServiceOptions?.skipBuilderSchemaValidation && isBuilderType(data?.type))
            ) {
                return
            }

            // To preserve the legacy API, data schemas override design schemas when there's a naming conflict
            if (
                namespace === designDt &&
                core.hasSchemaForDataType(designDt, dataTypeName) &&
                core.hasSchemaForDataType(dataDt, dataTypeName)
            ) {
                coreValidator(dataDt, dataTypeName, data)
                return
            }

            const namespacesToTry = [namespace, 'mobileHints', 'theSchemasFormerlyKnownAsCommon']
            const ns = _.find(namespacesToTry, possibleNamespace =>
                core.hasSchemaForDataType(possibleNamespace, dataTypeName)
            )
            if (!ns) {
                throw new CannotFindSchemaError(namespace, dataTypeName)
            }
            coreValidator(ns, dataTypeName, data)
        }

    const validate = createValidator(core.addDefaultsAndValidate)
    const validateNoDefaults = createValidator(core.validateNoDefaults)
    const validateStrict = createValidator(core.validateStrict)

    function isValid(dataTypeName: string, data: any, namespace: string): boolean {
        try {
            validate(dataTypeName, data, namespace)
            return true
        } catch (e) {
            return false
        }
    }

    const createItemAccordingToSchema = (schemaName: string, namespace: string, overrides = {}) => {
        if (core.hasSchemaForDataType(namespace, schemaName)) {
            const item = {...overrides, type: schemaName}
            try {
                validate(schemaName, item, namespace)
            } catch (e) {
                /*
                 Do not throw on purpose when creating item according to schema.
                 It is possible we will have a schema with required fields, but no defaults - and these will be added in later
                 This is backwards compatible behavior
                 */
            }
            return item
        }
    }

    function registerComponentDefinition(compType: string, componentDefinition: ComponentDefinition) {
        const newCompDef = componentDefinition[compType]
        if (!['Container', 'RepeaterContainer', 'Component'].includes(newCompDef.type)) {
            newCompDef.type = 'Component'
        }
        compDefs[compType] = newCompDef
        updateAliasMap(compType)
        updateSystemStyles(newCompDef)
    }

    function registerComponentSchemasCollection(
        compType: string,
        componentDefinition: ComponentDefinition,
        schemasCollection: SchemasCollection,
        skipCoreRegistration = false
    ) {
        registerComponentDefinition(compType, componentDefinition)
        if (!skipCoreRegistration) {
            core.addSchemaCollection(schemasCollection)
        }
    }

    function registerComponentDefinitionAndSchemas(
        compType: string,
        {componentDefinition, dataSchemas, propertiesSchemas, otherSchemas}: ComponentSchemasDefinition,
        allowOverrides = false,
        skipCoreRegistration = false
    ) {
        const schemaExists = !!compDefs[compType]
        if (schemaExists && !allowOverrides) {
            return
        }
        registerComponentDefinition(compType, componentDefinition)
        if (!skipCoreRegistration) {
            core.addDataTypesToExistingNamespace(
                DATA_TYPES.data,
                extendDataNodeSchemas(dataSchemas, DATA_TYPES.data),
                schemaExists
            )
            core.addDataTypesToExistingNamespace(
                DATA_TYPES.prop,
                extendDataNodeSchemas(propertiesSchemas, DATA_TYPES.prop),
                schemaExists
            )
            const moreSchemas = otherSchemas ?? {}
            Object.keys(moreSchemas).forEach(namespace => {
                if (core.hasNamespace(namespace)) {
                    core.addDataTypesToExistingNamespace(
                        namespace,
                        extendDataNodeSchemas(moreSchemas[namespace], namespace),
                        schemaExists
                    )
                } else {
                    throw new Error(
                        `Namespace ${namespace} does not exist. Creating new namespaces via registerComponentDefinitionAndSchemas is not supported.`
                    )
                }
            })
        }
    }

    function registerDataTypeSchema(schemasToRegister: SchemaFile, namespace: string) {
        if (core.hasNamespace(namespace)) {
            core.addDataTypesToExistingNamespace(namespace, extendDataNodeSchemas(schemasToRegister, namespace))
        } else {
            core.registerNamespace(namespace, schemasToRegister)
        }
    }

    function removeAdditionalProperties(namespace: string, dataTypeName: string, data: any): void {
        if (isAppVarsType(data?.type)) {
            return
        }

        core.removeAdditionalProperties(namespace, dataTypeName, data)
    }

    const containerTypesSet = new Set(['Page', 'Container', 'Document', 'RepeaterContainer', 'RefComponent'])

    const isContainer = (compKey: string) => containerTypesSet.has(getComponentType(compKey))
    const isPage = (compKey: string) => getComponentType(compKey) === 'Page'
    const isRepeater = (compKey: string) => getComponentType(compKey) === 'RepeaterContainer'
    const isRefComponent = (compKey: string) => getComponentType(compKey) === 'RefComponent'

    const getNamespaceConfig = (namespace: string): NamespaceConfig => {
        return schemaConfig.namespaceConfigs[namespace]
    }

    return {
        batchRegisterSchemas: core.batchRegisterSchemas,
        getDefinitionByPredicate,
        isSystemStyle,
        registerComponentSchemasCollection,
        registerComponentDefinitionAndSchemas,
        registerDataTypeSchema,
        getDefinition,
        getSchema,
        isDraftDataSchema: core.isDraftDataSchema,
        isDraftItem: core.isDraftItem,
        extractReferences: core.extractReferences,
        extractReferenceFieldsInfo: core.extractReferenceFieldsInfo,
        extractOwnedReferenceFieldsInfo: core.extractOwnedReferenceFieldsInfo,
        addReferences: core.addReferences,
        markAsBuilder: (name: string) => {
            registeredBuilderCollections.add(name)
        },
        isSchemaCollectionRegistered: (name: string) => {
            return registeredBuilderCollections.has(name)
        },
        hasNamespace: core.hasNamespace,
        hasSchemaForDataType: core.hasSchemaForDataType,
        isPermanentDataType: core.isPermanentDataType,
        removeAdditionalProperties,
        validate,
        // @ts-ignore
        addDefaultsAndValidate: validate,
        validateStrict,
        validateNoDefaults,
        createItemAccordingToSchema,
        isValid, // TODO: Adjust API to include errors like in old service
        isContainer,
        isPage,
        isRepeater,
        isRefComponent,
        getComponentType,
        getNamespaceConfig,
        whitelistCleanup,
        addDataTypesToExistingNamespace: core.addDataTypesToExistingNamespace,
        options: core.options ?? {}
    }
}

const getExperimentalOptions = (
    experimentInstance?: {isOpen(name: string): boolean},
    config?: Partial<DSConfig>
): SchemaServiceOptions => {
    return {
        enforceStrictValidationEverywhere: !!config?.enforceStrictValidationEverywhere,
        skipBuilderSchemaValidation: !!config?.skipBuilderSchemaValidation
    }
}

const computeKeyFromOptions = ({
    enforceStrictValidationEverywhere,
    skipBuilderSchemaValidation
}: SchemaServiceOptions) => {
    return `${enforceStrictValidationEverywhere}-${skipBuilderSchemaValidation}`
}

// The purpose of this closure is to hold different instances of the schema-service based on experiments.
// We need it because DMS is running only one instance of this service. Therefore, if we
// want to have experiments, we need multiple instances to be available at run time.
const experimentalInstances: Record<string, SchemaService> = {}

const getInstanceByExperiments = (
    experimentInstance?: {isOpen(name: string): boolean},
    dsConfig?: Partial<DSConfig>
): SchemaService => {
    const instanceOptions = getExperimentalOptions(experimentInstance, dsConfig)
    const instanceKey = computeKeyFromOptions(instanceOptions)

    if (!experimentalInstances[instanceKey]) {
        experimentalInstances[instanceKey] = createService(getUnifiedSchemasConfig(), instanceOptions)
    }

    return experimentalInstances[instanceKey]
}

export {
    SchemaValidationErrorDetails,
    SchemaValidationError,
    createSchemaCore,
    createService,
    getInstanceByExperiments,
    common,
    getExperimentalOptions
}
